<template>
  <ButtonWithSpinner
    v-bind="$attrs"
    :color="color"
    :status="status"
    :disabled="disabled || working"
    @click.stop.prevent="handleClick"
  >
    <slot>
      {{ displayText }}
    </slot>
  </ButtonWithSpinner>
</template>

<script>
import ButtonWithSpinner from '@/components/ButtonWithSpinner.vue'
import { ColorSchemes } from '@/utils/enum'
import { validateColorScheme } from '@/utils/propValidation'

export default {
  components: { ButtonWithSpinner },
  props: {
    text: { type: String, default: $t('Submit') },
    textWhileWorking: { type: String, default: $t('Saving') },
    autoSubmit: { type: Boolean, default: false },
    color: {
      type: String,
      default: ColorSchemes.PRIMARY,
      validator(value) {
        return validateColorScheme(value)
      },
    },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      working: false,
    }
  },
  computed: {
    displayText() {
      return this.working ? this.textWhileWorking : this.text
    },
    status() {
      return this.working ? 'working' : 'enabled'
    },
  },
  methods: {
    handleClick() {
      // Capture initial window.unsaved, which may have been set in other places globally
      // it is a global variable which will trigger on leave notifications
      const initialWindowUnsavedState = window.unsaved

      // Handle the submission of this button-instance's parent form
      if (this.autoSubmit) {
        window.unsaved = false
        const form = this.$el?.form

        const handleFormSubmission = (e) => {
          // If the form submission was blocked by another validation library
          if (e.defaultPrevented) {
            // We failed to save for some reason, so we restore the state to the original state.
            window.unsaved = initialWindowUnsavedState
          } else {
            this.working = true
          }
          // Remove the listener, so it can be re-added next time an instance of this button is clicked
          // Because multiple instances of this button can exist in the same form
          form.removeEventListener('submit', handleFormSubmission)
        }
        form.addEventListener('submit', handleFormSubmission)

        /**
         * Submit the parent form of this button
         * - we do not use form.submit() because it does not validate the form's content nor block if validation fails
         * - we cannot use the desired form.requestSubmit() because it is not supported by all browsers
         * - this is a polyfill which does the similar thing as requestSubmit()
         * see https://github.com/hotwired/turbo/pull/439/files
         */
        const fakeButton = document.createElement('button')
        fakeButton.type = 'submit'
        fakeButton.style.display = 'none'
        form.appendChild(fakeButton)
        fakeButton.click()
        fakeButton.remove()
      } else {
        // If button is not using the autoSubmit feature
        // jump straight to working mode
        this.working = true
      }
      this.$emit('click', this.working)
    },
  },
}
</script>
